/**
 * Answer helpers and the question-palette state machine.
 *
 * The palette uses the colour convention common to online exam clients, so a
 * student who has sat one before does not have to learn ours:
 *
 *   not_visited      grey    never opened
 *   not_answered     red     opened and left blank
 *   answered         green   has a response
 *   review           amber   flagged, no response
 *   answered_review  amber+  flagged, has a response
 *
 * "Answered" is decided by `isAnswered` below, not by "is there an object" —
 * an MSQ with every box unticked and a NAT with an empty field are both blank,
 * and showing them green would be a lie the student only discovers at submit.
 */
import type { AnswerResponse, QuestionType } from "@exam/shared";

export type PaletteState =
  | "not_visited"
  | "not_answered"
  | "answered"
  | "review"
  | "answered_review";

export function isAnswered(response: AnswerResponse | null | undefined): boolean {
  if (!response) return false;
  switch (response.type) {
    case "MCQ":
      return response.key !== null;
    case "MSQ":
      return response.keys.length > 0;
    case "NAT":
      return response.value !== null;
    case "CODE":
      // An untouched editor is not an answer. Whitespace counts as untouched
      // too — the starter code is the app's text, not the student's.
      return response.source.trim() !== "";
  }
}

export function paletteStateFor(
  response: AnswerResponse | null | undefined,
  markedForReview: boolean,
  visited: boolean,
): PaletteState {
  const answered = isAnswered(response);
  if (markedForReview) return answered ? "answered_review" : "review";
  if (answered) return "answered";
  return visited ? "not_answered" : "not_visited";
}

export const PALETTE_LABEL: Record<PaletteState, string> = {
  not_visited: "Not visited",
  not_answered: "Not answered",
  answered: "Answered",
  review: "Marked for review",
  answered_review: "Answered and marked",
};

/** An empty response of the right shape for a question type. */
export function emptyResponse(type: QuestionType): AnswerResponse {
  switch (type) {
    case "MCQ":
      return { type: "MCQ", key: null };
    case "MSQ":
      return { type: "MSQ", keys: [] };
    case "NAT":
      return { type: "NAT", value: null };
    case "CODE":
      // An empty CODE answer is an empty program with no run behind it. The
      // editor fills `source` from the question's starter code on first
      // render; this is the shape, not the content.
      return { type: "CODE", source: "", passed: 0, total: 0, results: [] };
  }
}

/** Toggles one key of an MSQ, keeping option order stable. */
export function toggleMsqKey(
  response: AnswerResponse | null,
  key: string,
  optionOrder: string[],
): AnswerResponse {
  const current =
    response?.type === "MSQ" ? new Set(response.keys) : new Set<string>();

  if (current.has(key)) current.delete(key);
  else current.add(key);

  return {
    type: "MSQ",
    keys: optionOrder.filter((k) => current.has(k)),
  };
}

/**
 * Parses a NAT field. Returns `undefined` for input that is not yet a number
 * (e.g. "-" or "3.") so the UI can keep the raw text without saving nonsense.
 */
export function parseNatValue(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  if (!/^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(trimmed)) return undefined;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : undefined;
}

/** Renders a stored response back into the NAT input's text. */
export function natValueToText(response: AnswerResponse | null): string {
  if (response?.type !== "NAT" || response.value === null) return "";
  return String(response.value);
}

export interface AnswerCounts {
  answered: number;
  unanswered: number;
  markedForReview: number;
  total: number;
}

export function countAnswers(
  questionIds: string[],
  responses: Map<string, AnswerResponse | null>,
  marked: Set<string>,
): AnswerCounts {
  let answered = 0;
  for (const id of questionIds) {
    if (isAnswered(responses.get(id))) answered += 1;
  }
  return {
    answered,
    unanswered: questionIds.length - answered,
    markedForReview: questionIds.filter((id) => marked.has(id)).length,
    total: questionIds.length,
  };
}
